/**
 * DrillTable — the served drill rows: columns are the union of row keys in
 * first-seen order (a Group union serves heterogeneous fm/ra rows by design —
 * models/drill.py rows contract), sortable via the carried SortableHeader +
 * useTableSort, CSV export via the carried csvExport utils. Rows that can
 * descend (rowDescent) are clickable; leaf rows are plain. Typed props (CD-7).
 */

import React from 'react'
import { Download } from 'lucide-react'
import SortableHeader from '../shared/SortableHeader'
import { useTableSort } from '../../hooks/useTableSort'
import { columnsOf, exportRows, formatDrillValue } from './drillTableFormat'
import type { DrillRow, PathSegment } from '../../types/metrics'
import { EM_DASH } from './cellFormat'

interface DrillTableProps {
  rows: DrillRow[]
  exportName: string // csv filename stem (metric·period·level)
  descentFor: (row: DrillRow) => PathSegment | null
  onDescend: (segment: PathSegment) => void
  /** Columns to leave OUT of the table — a measure another visual already carries.
   * Display only: the CSV still exports every served column, so nothing is lost. */
  hideColumns?: ReadonlySet<string>
  /** The column and direction the table opens on. Defaults to LineValue descending — the
   * useful read for a DIMENSION. ⚠ A TIME AXIS MUST OVERRIDE IT: ranking months by value
   * draws Jun · Jul · May · Apr and destroys the only thing the axis was for. */
  defaultSort?: { field: string; direction: 'asc' | 'desc' }
  /** Prepend a rank badge and append each row's share of the level's LineValue. The rank
   * is the row's position in the CURRENT sort, not a frozen ranking from some earlier
   * pass — re-sort by GM% and the badges follow, because a rank that disagrees with the
   * order it sits in is just a lie with a circle around it. */
  ranked?: boolean
}

/** Position in the current sort. Top three get the brand tint, as in the reference. */
function RankCell({ place }: { place: number }): React.JSX.Element {
  const tone = place <= 3 ? 'bg-info-bg text-brand' : 'bg-panel2 text-mut2'
  return (
    <td className="px-4 py-1.5">
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium tabular-nums ${tone}`}>
        {place}
      </span>
    </td>
  )
}

/** Share of the level's total value. The rows sum to the node, so these sum to 100%. */
function ShareCell({ value, total }: { value: DrillRow[string]; total: number }): React.JSX.Element {
  const share = typeof value === 'number' && total > 0 ? (100 * value) / total : null
  return (
    <td className="px-4 py-1.5 text-right tabular-nums text-mut">
      {share === null ? EM_DASH : `${share.toFixed(1)}%`}
    </td>
  )
}

function Row({
  row,
  columns,
  descentFor,
  onDescend,
  place,
  total,
}: Omit<DrillTableProps, 'rows' | 'exportName' | 'hideColumns' | 'ranked'> & {
  row: DrillRow
  columns: string[]
  place: number | null
  total: number | null
}): React.JSX.Element {
  const descent = descentFor(row)
  return (
    <tr
      onClick={descent ? () => onDescend(descent) : undefined}
      title={descent ? `Drill into ${descent.id}` : undefined}
      className={`border-b border-line2 last:border-b-0 ${descent ? 'cx-row cursor-pointer' : ''}`}
    >
      {place !== null && <RankCell place={place} />}
      {columns.map((column) => (
        <td
          key={column}
          className={`px-4 py-1.5 tabular-nums ${
            typeof row[column] === 'number' ? 'text-right' : 'text-left'
          }`}
        >
          {formatDrillValue(column, row[column])}
        </td>
      ))}
      {total !== null && <ShareCell value={row.LineValue} total={total} />}
    </tr>
  )
}

function ExportBar({ onExport }: { onExport: () => void }): React.JSX.Element {
  return (
    <div className="flex justify-end border-b border-line2 bg-panel2 px-2 py-1">
      <button
        type="button"
        onClick={onExport}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium text-mut hover:text-ink"
      >
        <Download size={12} /> Export CSV
      </button>
    </div>
  )
}

/** A plain heading for the two computed columns — neither is sortable, because rank IS
 * the current sort and share is monotone in LineValue, so a control there would do
 * nothing but promise something. */
const PLAIN_TH = 'px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-mut2'

function Head({ columns, rows, ranked, sort, toggle }: {
  columns: string[]
  rows: DrillRow[]
  ranked?: boolean
  sort: { field: string; direction: 'asc' | 'desc' }
  toggle: (field: string) => void
}): React.JSX.Element {
  return (
    <thead className="bg-panel2">
      <tr>
        {ranked && <th className={`${PLAIN_TH} text-left`}>Rank</th>}
        {columns.map((column) => (
          <SortableHeader
            key={column}
            label={column}
            field={column}
            currentField={sort.field}
            direction={sort.direction}
            onToggle={toggle}
            align={typeof rows[0][column] === 'number' ? 'right' : 'left'}
          />
        ))}
        {ranked && <th className={`${PLAIN_TH} text-right`}>% of total</th>}
      </tr>
    </thead>
  )
}

export default function DrillTable(props: DrillTableProps): React.JSX.Element {
  const { rows, exportName, descentFor, onDescend, hideColumns, ranked, defaultSort } = props
  const columns = columnsOf(rows, hideColumns)
  const { sorted, sort, toggle } = useTableSort(
    rows, defaultSort?.field ?? 'LineValue', defaultSort?.direction ?? 'desc'
  )
  if (rows.length === 0) {
    return (
      <p className="px-1 py-3 text-xs italic text-mut2">
        No rows served at this level — the path narrows to nothing.
      </p>
    )
  }
  const total = ranked
    ? rows.reduce((sum, row) => sum + (typeof row.LineValue === 'number' ? row.LineValue : 0), 0)
    : null
  return (
    <div className="cx-scroll overflow-x-auto rounded-xl border border-line">
      <ExportBar onExport={() => exportRows(sorted, columnsOf(rows), exportName)} />
      <table className="min-w-full text-xs" data-testid="drill-table">
        <Head columns={columns} rows={rows} ranked={ranked} sort={sort} toggle={toggle} />
        <tbody>
          {sorted.map((row, index) => (
            <Row
              key={index}
              row={row}
              columns={columns}
              descentFor={descentFor}
              onDescend={onDescend}
              place={ranked ? index + 1 : null}
              total={total}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
